import { useMutation } from '@apollo/client';
import {
    CONSENT_SOURCE,
    PRIVACY_POLICY_URL,
} from '@nicejob-library/data-platform-implementation/entities/User/CommunicationPreferences';
import { Button } from '@nicejob-library/design-system/core';
import { translate } from '@nicejob-library/internationalization';
import { trackClick } from '@nicejob-library/tracking';
import React, { ReactElement, useState } from 'react';
import { useOnboardingState } from '../../context/OnboardingStateContext';
import ONBOARDING_STATE_QUERIES from '../../queries/OnboardingState';
import {
    CommunicationPreferencesContainer,
    HeaderTextContainer,
    MainContainer,
    MainTextContainer,
    PreferenceCheckbox,
    PreferenceLinkText,
    PreferencesSection,
    PreferencesSectionTitle,
    WelcomeTextContainer,
} from './CommunicationPreferences.styles';

/**
 * CommunicationPreferences component for SSO onboarding flow
 * Captures marketing and ad targeting consent after OAuth signup
 */
export function CommunicationPreferences(): ReactElement {
    const { onboarding_state, onOnboardingStateChange } = useOnboardingState();
    const { onboarding_state_id, form_data } = onboarding_state || {};
    const { user_name } = form_data || {};

    const [marketing_opt_in, setMarketingOptIn] = useState<boolean>(false);
    const [ad_targeting_consent, setAdTargetingConsent] = useState<boolean>(false);
    const [is_loading, setIsLoading] = useState<boolean>(false);

    const [updateOnboardingState] = useMutation(
        ONBOARDING_STATE_QUERIES.Mutation.updateOnboardingState
    );

    async function onContinueClick(): Promise<void> {
        setIsLoading(true);

        trackClick({
            id: 'onboarding_communication_preferences_submit',
            type: 'action',
            is_anonymous: true,
        });

        // Store communication preferences on OnboardingState
        const prefs = {
            marketing_opt_in,
            ad_targeting_consent,
            consent_timestamp: new Date(),
            consent_source: CONSENT_SOURCE,
        };

        try {
            const response = await updateOnboardingState({
                variables: {
                    input: {
                        onboarding_state_id,
                        has_captured_communication_preferences: true,
                        communication_preferences: {
                            ...prefs,
                            consent_timestamp: prefs.consent_timestamp.toISOString(),
                        },
                    },
                },
            });

            if (response.data?.updateOnboardingState) {
                onOnboardingStateChange(response.data.updateOnboardingState);
            }
        } catch {
            // If mutation fails, update local state so the flow continues
            if (onboarding_state) {
                onOnboardingStateChange({
                    ...onboarding_state,
                    has_captured_communication_preferences: true,
                });
            }
        }

        setIsLoading(false);
    }

    const first_name = user_name?.split(' ')[0] || '';

    return (
        <CommunicationPreferencesContainer>
            <MainContainer>
                <MainTextContainer>
                    <WelcomeTextContainer>
                        {first_name
                            ? translate({
                                  namespace: 'login',
                                  key: 'onboarding.connect_gbp.welcome_name',
                                  params: { name: first_name },
                              })
                            : translate({
                                  namespace: 'login',
                                  key: 'common.header.welcome',
                              })}
                    </WelcomeTextContainer>
                    <HeaderTextContainer>
                        {translate({
                            namespace: 'login',
                            key: 'onboarding.communication_preferences.description',
                        })}
                    </HeaderTextContainer>
                </MainTextContainer>

                <PreferencesSection>
                    <PreferencesSectionTitle>
                        {translate({
                            namespace: 'login',
                            key: 'onboarding.register.optional_preferences.title',
                        })}
                    </PreferencesSectionTitle>

                    <PreferenceCheckbox
                        value='marketing_opt_in'
                        selected_checkboxes={marketing_opt_in ? ['marketing_opt_in'] : []}
                        onChange={(_, action): void => setMarketingOptIn(action === 'ADD_ITEM')}
                        label={
                            <span>
                                {translate({
                                    namespace: 'login',
                                    key: 'onboarding.register.optional_preferences.marketing.text',
                                })}{' '}
                                <PreferenceLinkText
                                    href={PRIVACY_POLICY_URL}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {translate({
                                        namespace: 'login',
                                        key:
                                            'onboarding.register.optional_preferences.privacy_policy',
                                    })}
                                </PreferenceLinkText>{' '}
                                {translate({
                                    namespace: 'login',
                                    key:
                                        'onboarding.register.optional_preferences.marketing.suffix',
                                })}
                            </span>
                        }
                    />

                    <PreferenceCheckbox
                        value='ad_targeting_consent'
                        selected_checkboxes={ad_targeting_consent ? ['ad_targeting_consent'] : []}
                        onChange={(_, action): void => setAdTargetingConsent(action === 'ADD_ITEM')}
                        label={
                            <span>
                                {translate({
                                    namespace: 'login',
                                    key:
                                        'onboarding.register.optional_preferences.ad_targeting.text',
                                })}{' '}
                                <PreferenceLinkText
                                    href={PRIVACY_POLICY_URL}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    {translate({
                                        namespace: 'login',
                                        key:
                                            'onboarding.register.optional_preferences.privacy_policy',
                                    })}
                                </PreferenceLinkText>
                                .
                            </span>
                        }
                    />
                </PreferencesSection>

                <Button variant='action' loading={is_loading} onClick={onContinueClick}>
                    {translate({
                        namespace: 'login',
                        key: 'onboarding.register_company.button.continue',
                    })}
                </Button>
            </MainContainer>
        </CommunicationPreferencesContainer>
    );
}
